import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { initPixels, trackEvent, trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest } from '../utils/offerBus';
import { Offer } from '../data/product';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const [priceUnlocked, setPriceUnlocked] = useState(false);
  const objection = useObjectionTrigger();
  useEffect(() => {
    initPixels();
  }, []);
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackEvent('InitiateCheckout', {
      content_name: id
    });
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    setActiveObjection(id);
    trackCustom(choice.event);
    // The 20% single-unit rescue only exists inside the price path.
    if (id === 'price') setPriceUnlocked(true);
    setSelectedOffer(choice.recommend);
    objection.close();
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." Take the 5% code and go straight to the packs.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    trackEvent('AddToCart', {
      content_name: 'GritVue X1'
    });
    objection.close();
    window.setTimeout(() => {
      document.getElementById('offer')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-985" data-mp="3nv3sv01cueyc">
      {showAnnouncementBar && <AnnouncementBar data-id="element-986" data-mp="5ljnvgawct1l" />}
      <SiteNav data-id="element-987" data-mp="ojil7lvfubj" />
      <main className="pb-24 lg:pb-0" data-id="element-988" data-mp="o1nieay3m57">
        {/* 1. Promise + product */}
        <Hero data-id="element-989" data-mp="1h2t6k0m7bh1u" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-990" data-mp="1h1i6aarysrfh" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-991" data-mp="1c2opa8dgyiak" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-992" data-mp="27eoiq60qxmrb" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-993" data-mp="2ccpyoi2vl5yk" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-994" data-mp="2cf2g8u4q1eb3" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-995" data-mp="2hb9myp4xytu3" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-996" data-mp="2mbfi4faf6ygx" />
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-997" data-mp="2qd0p5k09wij7" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-998" data-mp="2qaobhj1jsuby" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-999" data-mp="2va983mt8wqli" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1000" data-mp="303loxaront3p" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1001" data-mp="350c2nrg0mcly" />
        {/* 11. The decision */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1002" data-mp="3a104l85s8duy" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} priceUnlocked={priceUnlocked} onChooseAnother={objection.reopen} data-id="element-1003" data-mp="12eq9gomuovvi" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1004" data-mp="tea9vq98a8vx" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1005" data-mp="tf37o1q7lo1x" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1006" data-mp="oh1m9a5b9cna" />
      </main>
      <SiteFooter data-id="element-1007" data-mp="1m2pk7zhf030x" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1008" data-mp="1m38ucu0tpn4e" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1009" data-mp="1m4s8of2x5o3j" />
    </div>;
}